import React, { useState, useEffect } from 'react';
import { useWizard } from '../../contexts/WizardContext';
import { isStellarNetwork } from '../../config/networks';
import { fetchStellarContractFunctions } from '../../services/stellar';
import './Step4ABIInput.css';

const Step4ABIInput: React.FC = () => {
  const { currentStep, wizardData, updateWizardData } = useWizard();
  const [abi, setAbi] = useState(wizardData.contractABI);
  const [error, setError] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [isFetchingSpec, setIsFetchingSpec] = useState(false);
  const [fetchedFunctions, setFetchedFunctions] = useState<string[]>([]);
  const [specFetchKey, setSpecFetchKey] = useState(0);
  const stellar = isStellarNetwork(wizardData.chain);

  useEffect(() => {
    if (!stellar || !wizardData.chain || !wizardData.contractAddress) {
      return;
    }

    const specAddress =
      wizardData.isTrustlineFirewall && wizardData.firewallTargetAddress
        ? wizardData.firewallTargetAddress
        : wizardData.contractAddress;

    let cancelled = false;

    const fetchSpec = async () => {
      setIsFetchingSpec(true);
      setError('');
      setFetchedFunctions([]);
      try {
        const functions = await fetchStellarContractFunctions(
          wizardData.chain!,
          specAddress
        );
        if (cancelled) return;

        if (functions.length === 0) {
          setError('No callable functions found in the on-chain SEP-48 contract spec');
          updateWizardData({ contractABI: '', methodMappings: [] });
          return;
        }

        // Store a pseudo-ABI so the existing policy-mapping step can parse methods
        const pseudoAbi = functions.map(fn => ({
          type: 'function',
          name: fn.name,
          inputs: fn.inputs.map(input => ({
            name: input.name,
            type: input.type,
          })),
          stateMutability: 'nonpayable',
        }));

        const abiJson = JSON.stringify(pseudoAbi, null, 2);
        setAbi(abiJson);
        setFetchedFunctions(functions.map(fn => fn.prototype));
        updateWizardData({
          contractABI: abiJson,
          methodMappings: [],
        });
      } catch (err: any) {
        if (cancelled) return;
        console.error('Failed to fetch Stellar contract spec:', err);
        setError(err.message || 'Failed to fetch on-chain contract spec (SEP-48)');
        updateWizardData({ contractABI: '', methodMappings: [] });
      } finally {
        if (!cancelled) {
          setIsFetchingSpec(false);
        }
      }
    };

    fetchSpec();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    stellar,
    wizardData.chain,
    wizardData.contractAddress,
    wizardData.isTrustlineFirewall,
    wizardData.firewallTargetAddress,
    specFetchKey,
  ]);

  const handleABIChange = (value: string) => {
    setAbi(value);
    setError('');

    if (value.trim()) {
      try {
        const parsed = JSON.parse(value.trim());

        if (!Array.isArray(parsed)) {
          setError('ABI must be a JSON array');
          return;
        }

        const hasFunctions = parsed.some((item: any) => item.type === 'function');
        if (!hasFunctions) {
          setError('ABI should contain at least one function definition');
          return;
        }

        updateWizardData({ contractABI: value.trim() });
      } catch (err) {
        setError('Invalid JSON format');
      }
    }
  };

  if (stellar) {
    return (
      <div className="wizard-step step4-abi-input">
        <h2 className="wizard-step-title">Contract Interface</h2>
        <p className="wizard-step-description">
          {wizardData.isTrustlineFirewall
            ? 'Trustline Firewall detected, the interface of the target contract is fetched automatically from on-chain WASM via SEP-48.'
            : 'The contract interface is fetched automatically from on-chain WASM via SEP-48.'}
        </p>

        {isFetchingSpec ? (
          <div className="wizard-loading">Fetching on-chain contract spec…</div>
        ) : error ? (
          <div className="step4-error-container">
            <div className="wizard-error">{error}</div>
            <button
              type="button"
              className="step4-retry-btn"
              onClick={() => setSpecFetchKey(k => k + 1)}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="step4-success-section">
            <div className="step4-success-icon">✓</div>
            <h3>Contract Spec Loaded</h3>
            {wizardData.isTrustlineFirewall && wizardData.firewallTargetAddress && (
              <p className="step4-success-message">
                Spec source (protected target): <code>{wizardData.firewallTargetAddress}</code>
              </p>
            )}
            <p className="step4-success-message">
              Found {fetchedFunctions.length} callable function
              {fetchedFunctions.length === 1 ? '' : 's'} from the on-chain interface.
            </p>
            <ul className="step4-fetched-functions">
              {fetchedFunctions.map(fn => (
                <li key={fn}>
                  <code>{fn}</code>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="wizard-step step4-abi-input">
      <h2 className="wizard-step-title">Contract Interface</h2>
      <p className="wizard-step-description">
        Paste the ABI (Application Binary Interface) of your smart contract.
        This will be used to identify the methods that can be protected.
        If your contract is protected through a Trustline Firewall, you should paste the ABI of your <strong>protected</strong> contract here.
      </p>

      <div className="wizard-form-group">
        <label htmlFor="contract-abi">Contract ABI (JSON) *</label>
        <textarea
          id="contract-abi"
          value={abi}
          onChange={(e) => handleABIChange(e.target.value)}
          placeholder='[{"type": "function", "name": "transfer", ...}, ...]'
          className={error ? 'error' : ''}
          required
        />
        {error && <div className="wizard-error">There are errors in your ABI: {error}</div>}
        {!error && abi && (
          <div className="wizard-success">Your ABI is valid</div>
        )}
      </div>

      <div className="step4-instructions">
        <button
          type="button"
          onClick={() => setShowInstructions(!showInstructions)}
          className="step4-instructions-toggle"
        >
          {showInstructions ? '▽' : '▷'} How to get your contract ABI
        </button>
        {showInstructions && (
          <div className="step4-instructions-content">
            <div className="step4-instruction-item">
              <h4 className="step4-instruction-title">Remix IDE</h4>
              <ol className="step4-instruction-steps">
                <li>Open your contract in Remix IDE</li>
                <li>Compile your contract</li>
                <li>In the "Solidity Compiler" tab, find your compiled contract</li>
                <li>Click on the contract name to expand it</li>
                <li>Copy the content of the "ABI" field (JSON array)</li>
              </ol>
            </div>

            <div className="step4-instruction-item">
              <h4 className="step4-instruction-title">Foundry</h4>
              <ol className="step4-instruction-steps">
                <li>After compiling with <code>forge build</code>, navigate to <code>out/YourContract.sol/YourContract.json</code></li>
                <li>Open the JSON file</li>
                <li>Copy the <code>"abi"</code> field value (the JSON array)</li>
              </ol>
            </div>

            <div className="step4-instruction-item">
              <h4 className="step4-instruction-title">Hardhat</h4>
              <ol className="step4-instruction-steps">
                <li>After compiling with <code>npx hardhat compile</code>, navigate to <code>artifacts/contracts/YourContract.sol/YourContract.json</code></li>
                <li>Open the JSON file</li>
                <li>Copy the <code>"abi"</code> field value (the JSON array)</li>
              </ol>
            </div>

            <div className="step4-instruction-item">
              <h4 className="step4-instruction-title">Block Explorer</h4>
              <ol className="step4-instruction-steps">
                <li>Navigate to your contract on a block explorer (Etherscan, Polygonscan, etc.)</li>
                <li>Go to the "Contract" tab</li>
                <li>Scroll down to find the "Contract ABI" section</li>
                <li>Copy the ABI JSON array</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step4ABIInput;
